/** @format */

import axios from 'axios';
import appConfig from 'config/app';

// axios.defaults.withCredentials = true;

/**
 * Common function to call Fria Maklare API endpoints
 *
 * @param url - Api endpoint to call ex:- healthcheck, registerBroker etc
 * @param httpType - API endpoint HTTP type to use ex:- GET, POST, PUT, DELETE
 * @param data - data object to be passed to the API
 * @param auth -boolean- whether auth params to be included or not - default false
 * @param requestContentType - Request content type ex:- application/json, multipart/formdata etc
 *        default is application/json
 *
 * @returns Object
 *
 * @author Sandun Munasinghe
 * @since 16-08-2022
 */
const callFriaMaklareApi = async (
  endpointUrl,
  httpType = 'GET',
  data = null,
  auth = false,
  requestContentType = 'application/json'
) => {
  let requestParamsObj = {
    timeout: 20000
  };

  const url = `${appConfig.baseApiUrl}/${endpointUrl}`;
  const accessToken = '';

  if (httpType == 'GET' || httpType == 'DELETE') {
    if (data) {
      requestParamsObj.params = data;
    }

    if (auth) {
      requestParamsObj.headers = {
        Authorization: `Bearer ${accessToken}`
      };
    }
    if (httpType == 'GET') {
      return axios.get(url, requestParamsObj);
    } else {
      return axios.delete(url, requestParamsObj);
    }
  } else if (httpType == 'POST' || httpType == 'PUT') {
    let postRequestObj = {
      method: httpType.toLowerCase(),
      url,
      headers: {
        'Content-Type': requestContentType
      }
    };
    if (data) {
      postRequestObj.data = data;
    }

    if (auth) {
      postRequestObj.headers = {
        ...postRequestObj.headers,
        Authorization: `Bearer ${accessToken}`
      };
    }

    return axios(postRequestObj);
  }
};

/**
 *
 * Calls the register broker API in Fria Maklare
 *
 * @param {string} firstName
 * @param {string} lastname
 * @param {string} email
 * @param {boolean} isAbroadBroker
 *
 * @author Sandun Munasinghe
 * @since 16-08-2022
 */
export const registerUserApiService = async (
  firstName,
  lastName,
  email,
  isAbroadBroker = false,
  extra
) => {
  return callFriaMaklareApi('registerBroker', 'POST', {
    firstName,
    lastName,
    email,
    extra,
    isAbroadBroker
  });
};

export const onboardBrokerService = async (formData) => {
  return callFriaMaklareApi(
    'onboardBroker',
    'PUT',
    formData,
    false,
    'multipart/form-data'
  );
};

export const fetchBrokerProfileService = async ({ brokerId, profileRef }) => {
  const response = await callFriaMaklareApi(
    'brokerData',
    'GET',
    { brokerId, profileRef },
    false
  );
  return response;
};

export const fetchBrokerProfileBySlugService = async ({
  brokerSlug,
  profileRef
}) => {
  const response = await callFriaMaklareApi(
    'brokerDataBySlug',
    'GET',
    { brokerSlug, profileRef },
    false
  );
  return response;
};

export const fetchBrokerObjectsService = async ({
  brokerId,
  profileRef,
  page = 1,
  statuses
}) => {
  const response = await callFriaMaklareApi(
    'listBrokerObjects',
    'GET',
    { brokerId, profileRef, page, statuses },
    false
  );
  return response;
};
export const fetchBrokerObjectsBySlugService = async ({
  brokerSlug,
  profileRef,
  page = 1,
  statuses
}) => {
  const response = await callFriaMaklareApi(
    'listBrokerObjectsBySlug',
    'GET',
    { brokerSlug, profileRef, page, statuses },
    false
  );
  return response;
};

export const fetchBrokerObjectService = async ({
  objectId,
  vitecObjectId = null,
  profileRef
}) => {
  const response = await callFriaMaklareApi(
    'objectData',
    'GET',
    { objectId, profileRef, vitecId: vitecObjectId },
    false
  );
  return response;
};

export const fetchViewingTimeSlotsService = async ({
  customerId,
  objectId
}) => {
  const response = await callFriaMaklareApi(
    `viewingTimeSlots?customerId=${customerId}&objectId=${objectId}`,
    'GET',
    false
  );
  return response;
};

/**
 *
 * Calls the create lead API in Fria Maklare
 *
 *
 * @author Daniel A
 * @since 16-10-2024
 */
export const createLeadApiService = async (payload) => {
  return callFriaMaklareApi('leads', 'POST', payload);
};

export const searchPropertiesApiService = async (payload) => {
  const response = await callFriaMaklareApi(
    'propertyObject',
    'GET',
    payload,
    false
  );
  return response;
};

export const logErrorApiService = async (payload) => {
  const response = await callFriaMaklareApi('logError', 'POST', payload, false);
  return response;
};

export const searchLeadsApiService = async (payload) => {
  const response = await callFriaMaklareApi('leads', 'GET', payload, false);
  return response;
};
